/*
 * croma-motion.js — P1 GSAP motion layer for the CromaTCG homepage.
 * Additive, deferred, post-LCP. Extends (never duplicates) the P0 pure-CSS
 * motion pack. All effects are transform/opacity only (CLS-safe).
 *
 * Guardrails:
 *  - Hard reduced-motion early return: ships nothing for those users.
 *  - Desktop-only effects (tilt, parallax) gated via gsap.matchMedia.
 *  - Never sets opacity:0 in server CSS; hidden start states are set in JS
 *    AFTER load, so no-JS / Googlebot always sees fully painted content.
 *  - Re-inits on the theme-editor shopify:section:load event.
 */
(function () {
  'use strict';

  // 1) HARD reduced-motion guard — ship nothing.
  if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Bail quietly if the engine failed to load (defensive; never throws).
  if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return;

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;

  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ limitCallbacks: true });

  var DESKTOP = '(min-width:981px) and (pointer:fine)';

  // Track disposables so we can tear down + re-init cleanly in the editor.
  var mm = null;              // gsap.matchMedia instance (desktop effects)
  var cleanups = [];          // extra teardown fns for non-matchMedia effects

  function toArr(sel) { return gsap.utils.toArray(sel); }

  /* ------------------------------------------------------------------ *
   * 2) Community store-video scroll play/pause (all viewports).
   *    Videos are autoplay:false preload:none — saves bandwidth vs autoplay.
   * ------------------------------------------------------------------ */
  function initVideos() {
    toArr('.croma-loc__media video').forEach(function (video) {
      function play() {
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {}); // swallow rejection
      }
      function pause() { try { video.pause(); } catch (e) {} }

      var st = ScrollTrigger.create({
        trigger: video,
        start: 'top 90%',
        end: 'bottom 10%',
        onEnter: play,
        onEnterBack: play,
        onLeave: pause,
        onLeaveBack: pause
      });
      cleanups.push(function () { st.kill(); pause(); });
    });
  }

  /* ------------------------------------------------------------------ *
   * 3) ScrollTrigger.batch staggered reveals (SEO-safe).
   *    Hidden start state is set in JS only (never in server CSS).
   *    Only targets grids that have NO existing P0 .croma-reveal, so we
   *    never double-animate.
   * ------------------------------------------------------------------ */
  function initReveals() {
    // Only orchestrate elements that have NO P0 CSS reveal. A CSS animation
    // with fill:both wins the cascade over GSAP's inline transform/opacity, so
    // mixing the two leaves cards stuck at their hidden start state. Product
    // cards have no CSS reveal; auth/review cards already animate via P0
    // .croma-reveal, so we leave those to CSS.
    var els = toArr('.croma-pcard');
    if (!els.length) return;

    // Set hidden start state AFTER load — safe for crawlers/no-JS.
    gsap.set(els, { opacity: 0, y: 20 });

    var batch = ScrollTrigger.batch(els, {
      start: 'top 88%',
      onEnter: function (b) {
        gsap.to(b, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.06, overwrite: true });
      },
      once: true
    });

    cleanups.push(function () {
      batch.forEach(function (st) { st.kill(); });
      gsap.set(els, { clearProps: 'opacity,transform' }); // restore visible state
    });
  }

  /* ------------------------------------------------------------------ *
   * Desktop-only effects (tilt + scrub parallax) via gsap.matchMedia.
   * ------------------------------------------------------------------ */
  function initDesktop() {
    mm = gsap.matchMedia();

    mm.add(DESKTOP, function () {
      /* 4) Hero pointer-tilt (Layer 2).
       *    The drop has the P0 CSS entrance (croma-hero-rise, fill:both), which
       *    permanently overrides GSAP's inline transform. So we let the entrance
       *    finish, THEN kill it and hand transform control to GSAP cleanly. */
      var drop = document.querySelector('.croma-hero__drop');
      var tiltCleanup = null;
      if (drop) {
        var startTilt = function () {
          drop.style.animation = 'none';               // release CSS entrance
          gsap.killTweensOf(drop);
          gsap.set(drop, { clearProps: 'all' });       // wipe cached from-state
          gsap.set(drop, { opacity: 1, transformPerspective: 900, transformStyle: 'preserve-3d' });

          var rx = gsap.quickTo(drop, 'rotationX', { duration: 0.4, ease: 'power2' });
          var ry = gsap.quickTo(drop, 'rotationY', { duration: 0.4, ease: 'power2' });
          var sc = gsap.quickTo(drop, 'scale', { duration: 0.4, ease: 'power2' });
          var MAX = 10; // ±10deg — noticeable lean, not extreme
          function onMove(e) {
            var r = drop.getBoundingClientRect();
            var px = (e.clientX - r.left) / r.width;   // 0..1
            var py = (e.clientY - r.top) / r.height;   // 0..1
            ry(gsap.utils.clamp(-MAX, MAX, (px - 0.5) * 2 * MAX));
            rx(gsap.utils.clamp(-MAX, MAX, (0.5 - py) * 2 * MAX));
            sc(1.03); // subtle lift toward the viewer
          }
          function onLeave() { rx(0); ry(0); sc(1); }
          drop.addEventListener('mousemove', onMove);
          drop.addEventListener('mouseleave', onLeave);
          tiltCleanup = function () {
            drop.removeEventListener('mousemove', onMove);
            drop.removeEventListener('mouseleave', onLeave);
          };
        };
        // Start after the entrance completes; delayedCall is the fallback in
        // case the deferred script loads after animationend already fired.
        var tiltStarted = false;
        var startOnce = function () { if (tiltStarted) return; tiltStarted = true; startTilt(); };
        drop.addEventListener('animationend', startOnce, { once: true });
        gsap.delayedCall(1.3, startOnce);
      }

      /* 5) Set-tiles logo-plate scrub parallax. */
      var plates = toArr('.croma-tiles--set .croma-gtile__img img');
      var scrubs = plates.map(function (img) {
        return gsap.fromTo(img,
          { y: -8 },
          {
            y: 8,
            ease: 'none',
            scrollTrigger: {
              trigger: img.closest('.croma-gtile') || img,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true
            }
          }
        );
      });

      // matchMedia auto-reverts these on cleanup, but return an explicit
      // teardown for completeness.
      return function () {
        if (tiltCleanup) tiltCleanup();
        if (drop) {
          drop.style.animation = '';                   // restore CSS entrance
          gsap.set(drop, { clearProps: 'transform,perspective,opacity' });
        }
        scrubs.forEach(function (t) { if (t.scrollTrigger) t.scrollTrigger.kill(); t.kill(); });
      };
    });
  }

  /* ------------------------------------------------------------------ */
  function initAll() {
    initVideos();
    initReveals();
    initDesktop();
    ScrollTrigger.refresh();
  }

  function teardownAll() {
    cleanups.forEach(function (fn) { try { fn(); } catch (e) {} });
    cleanups = [];
    if (mm) { mm.revert(); mm = null; }
  }

  // Init once the DOM is ready (script is deferred, but be defensive).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  /* ------------------------------------------------------------------ *
   * Theme-editor safety: rebuild on section load so merchants never see
   * blank/broken sections while editing.
   * ------------------------------------------------------------------ */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      teardownAll();
      initAll();
      ScrollTrigger.refresh();
    });
    document.addEventListener('shopify:section:unload', function () {
      teardownAll();
    });
  }
})();
